import React, { useMemo, useState } from "react";
import { RecBar, RecIcon } from "../../materials/MaterialRecordParts";
import { PRIORITY_OPTIONS } from "../../../helpers/tasks/taskModel";
import { DueChip, initialsOf, PriorityChip } from "../TaskParts";
import {
  applyBulkPatch,
  draftValidationErrors,
  groupDrafts,
  isDraftApprovable,
  resolveClarifyDraft,
  TASK_ACTION,
  updateDraftById,
} from "../../../taskIntelligence/taskDraftModel";
import { TaskDraftQuickEdit } from "./TaskDraftQuickEdit";
import "./taskBuilder.css";

const BULK_ACTIONS = [
  { key: "assignee", label: "Assign", icon: "user" },
  { key: "due", label: "Due Date", icon: "calendar" },
  { key: "location", label: "Location", icon: "pin" },
  { key: "priority", label: "Priority", icon: "flag" },
];

function BulkPanel({ action, members, locations, onApply, onClose }) {
  const [value, setValue] = useState("");
  if (!action) return null;

  function apply() {
    if (action === "assignee") onApply({ assigneeId: value, assigneeName: members.find((m) => m.id === value)?.name || "" });
    else if (action === "due") onApply({ dueDate: value });
    else if (action === "location") onApply({ locationId: value });
    else if (action === "priority") onApply({ priority: value });
    onClose();
  }

  return (
    <div className="tbl-bulkpanel" role="dialog" aria-label={`Bulk ${action}`}>
      {action === "assignee" ? (
        <select className="rec-input" value={value} onChange={(e) => setValue(e.target.value)} autoFocus>
          <option value="">Unassigned</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      ) : null}
      {action === "due" ? (
        <input className="rec-input" type="date" value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
      ) : null}
      {action === "location" ? (
        <select className="rec-input" value={value} onChange={(e) => setValue(e.target.value)} autoFocus>
          <option value="">No location</option>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      ) : null}
      {action === "priority" ? (
        <select className="rec-input" value={value} onChange={(e) => setValue(e.target.value)} autoFocus>
          {PRIORITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : null}
      <div className="tbl-bulkpanel__actions">
        <button type="button" className="rec-btn rec-btn--ghost" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="rec-btn rec-btn--primary" onClick={apply}>
          Apply
        </button>
      </div>
    </div>
  );
}

function DraftRow({ draft, existingTask, locationName, onToggle, onOpen }) {
  const blocked = !isDraftApprovable(draft);
  return (
    <div className={`tk-row tbl-row${draft.action === TASK_ACTION.CLARIFY ? " tbl-row--clarify" : ""}`} data-testid="task-draft-row">
      <button
        type="button"
        className="tbl-check-wrap"
        role="checkbox"
        aria-checked={draft.selected}
        aria-label={draft.selected ? `Deselect ${draft.title}` : `Select ${draft.title}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggle(draft.id);
        }}
      >
        <span className={`tbl-check${draft.selected ? " is-on" : ""}`} aria-hidden="true">
          {draft.selected ? <RecIcon name="check" /> : null}
        </span>
      </button>
      <button type="button" className="tk-row__main" onClick={() => onOpen(draft)} aria-label={`Edit ${draft.title || "task"}`}>
        <span className="tk-row__title">
          {draft.title || <em>Needs a title</em>}
          {!draft.explicit ? <span className="tbl-badge tbl-badge--suggest">✨ Ashley suggests</span> : null}
        </span>
        <span className="tk-row__meta">
          <PriorityChip priority={draft.priority} />
          {draft.dueDate ? <DueChip task={{ due_date: draft.dueDate, status: "open" }} /> : null}
          {locationName ? <span className="tk-row__loc">{locationName}</span> : null}
          {draft.action === TASK_ACTION.UPDATE && existingTask ? (
            <span className="tbl-badge tbl-badge--update">Updates "{existingTask.title}"</span>
          ) : null}
        </span>
        {draft.action === TASK_ACTION.CLARIFY ? <span className="tbl-clarify">{draft.clarifyPrompt}</span> : null}
        {blocked && draft.action !== TASK_ACTION.CLARIFY ? (
          <span className="tbl-clarify">{Object.values(draftValidationErrors(draft))[0]}</span>
        ) : null}
      </button>
      {draft.assigneeName ? (
        <span className="tk-avatar" title={draft.assigneeName} aria-label={`Assigned to ${draft.assigneeName}`}>
          {initialsOf(draft.assigneeName)}
        </span>
      ) : null}
    </div>
  );
}

export function TaskBuilderReviewScreen({
  projectName,
  drafts,
  setDrafts,
  noAction,
  notes,
  existingTasksById,
  members,
  locations,
  creating,
  createError,
  onCreate,
  onBack,
}) {
  const [bulkAction, setBulkAction] = useState(null);
  const [editingId, setEditingId] = useState("");
  const [showNoAction, setShowNoAction] = useState(false);
  const [extraNoAction, setExtraNoAction] = useState([]);

  const selectedIds = useMemo(() => new Set(drafts.filter((d) => d.selected).map((d) => d.id)), [drafts]);
  const selectedCount = selectedIds.size;
  const selectedDrafts = useMemo(() => drafts.filter((d) => d.selected), [drafts]);
  const blockers = selectedDrafts.filter((d) => !isDraftApprovable(d));
  const groups = useMemo(() => groupDrafts(drafts), [drafts]);
  const locationNameById = useMemo(() => new Map(locations.map((l) => [l.id, l.name])), [locations]);
  const editingDraft = drafts.find((d) => d.id === editingId);

  function toggle(id) {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, selected: !d.selected } : d)));
  }

  function applyBulk(patch) {
    setDrafts((prev) => applyBulkPatch(prev, selectedIds, patch));
  }

  function promoteNoAction(item) {
    const promoted = {
      ...item,
      action: TASK_ACTION.CREATE,
      explicit: true,
      description: item.description || `Originally skipped: ${item.evidence || ""}`.trim(),
    };
    setExtraNoAction((prev) => [...prev, item]);
    setDrafts((prev) => [
      ...prev,
      {
        id: `${item.existingTaskId || "promoted"}-${prev.length}`,
        group: promoted.group || "General",
        title: promoted.title,
        description: promoted.description,
        action: TASK_ACTION.CREATE,
        explicit: true,
        assigneeId: promoted.assigneeId || "",
        assigneeName: promoted.assigneeName || "",
        dueDate: promoted.dueDate || "",
        priority: promoted.priority || "",
        locationId: promoted.locationId || "",
        existingTaskId: null,
        clarifyPrompt: null,
        evidence: promoted.evidence || "",
        selected: true,
      },
    ]);
  }

  const visibleNoAction = noAction.filter((item) => !extraNoAction.includes(item));

  return (
    <div className="screen nx-screen rec-page tbl-screen" data-testid="task-builder-review">
      <RecBar title="Ashley's Recommended Tasks" onBack={onBack} />
      {projectName ? <div className="tk-subtitle">{projectName}</div> : null}
      <div className="tbl-body">
        <div className="tbl-subtitle">{drafts.length} task{drafts.length === 1 ? "" : "s"} suggested</div>

        {createError ? (
          <div className="rec-banner" role="alert">
            {createError}
          </div>
        ) : null}

        {selectedCount > 0 ? (
          <div className="tbl-bulkbar">
            <span className="tbl-bulkbar__count">{selectedCount} selected</span>
            <div className="tbl-bulkbar__actions">
              {BULK_ACTIONS.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  className="tbl-bulkbtn"
                  onClick={() => setBulkAction(bulkAction === action.key ? null : action.key)}
                >
                  <RecIcon name={action.icon} /> {action.label}
                </button>
              ))}
            </div>
            <BulkPanel
              action={bulkAction}
              members={members}
              locations={locations}
              onApply={applyBulk}
              onClose={() => setBulkAction(null)}
            />
          </div>
        ) : null}

        {groups.length === 0 ? (
          <div className="pj-empty" data-testid="task-builder-empty">
            Ashley didn't find any tasks to propose from that brief.
          </div>
        ) : (
          groups.map((group) => (
            <section key={group.group} className="tk-group" data-testid="task-builder-group">
              <div className="tk-group__head">
                <span className="tk-group__name">{group.group}</span>
                <span className="tk-group__sub">{group.drafts.length} task{group.drafts.length === 1 ? "" : "s"}</span>
              </div>
              <div className="tk-list">
                {group.drafts.map((draft) => (
                  <DraftRow
                    key={draft.id}
                    draft={draft}
                    existingTask={draft.existingTaskId ? existingTasksById.get(draft.existingTaskId) : null}
                    locationName={draft.locationId ? locationNameById.get(draft.locationId) : ""}
                    onToggle={toggle}
                    onOpen={(d) => setEditingId(d.id)}
                  />
                ))}
              </div>
            </section>
          ))
        )}

        {visibleNoAction.length ? (
          <div className="tbl-noaction">
            <button type="button" className="tk-link" onClick={() => setShowNoAction((v) => !v)}>
              {showNoAction ? "Hide" : "Show"} what Ashley didn't create tasks for ({visibleNoAction.length})
            </button>
            {showNoAction ? (
              <ul className="tbl-noaction__list">
                {visibleNoAction.map((item, index) => (
                  <li key={index} className="tbl-noaction__item">
                    <div>
                      <strong>{item.title || item.evidence}</strong>
                      {item.description ? <div className="tbl-hint">{item.description}</div> : null}
                    </div>
                    <button type="button" className="tk-link" onClick={() => promoteNoAction(item)}>
                      Create anyway
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {notes.length ? (
          <ul className="tbl-notes">
            {notes.map((note, index) => (
              <li key={index}>{note}</li>
            ))}
          </ul>
        ) : null}
        <div className="pj-pagepad" aria-hidden="true" />
      </div>

      <div className="rec-actions">
        {blockers.length ? (
          <div className="tbl-hint tbl-blockhint">
            {blockers.length} selected task{blockers.length === 1 ? "" : "s"} need{blockers.length === 1 ? "s" : ""} more information first.
          </div>
        ) : null}
        <button
          type="button"
          className="rec-btn rec-btn--primary"
          disabled={selectedCount === 0 || blockers.length > 0 || creating}
          onClick={() => onCreate(selectedDrafts)}
        >
          <RecIcon name="check" /> {creating ? "Creating…" : `Create ${selectedCount} Task${selectedCount === 1 ? "" : "s"}`}
        </button>
      </div>

      {editingDraft ? (
        <TaskDraftQuickEdit
          draft={editingDraft}
          members={members}
          locations={locations}
          onCancel={() => setEditingId("")}
          onSave={(patch) => {
            setDrafts((prev) =>
              updateDraftById(
                prev,
                editingDraft.id,
                editingDraft.action === TASK_ACTION.CLARIFY ? resolveClarifyDraft(editingDraft, patch) : patch,
              ),
            );
            setEditingId("");
          }}
        />
      ) : null}
    </div>
  );
}
